/**
 * Connection commit helpers for the interactive protocol DAG.
 */

import { useCallback } from "react";
import type { Connection } from "@xyflow/react";
import type { ProtocolConnection } from "../../api/clinicalTrialsApiTypes";
import {
  compatibleConnectionTypes,
  type ProtocolConnectionType,
  type ProtocolNodeKind,
} from "./protocolDagConnectionRules";
import type { PendingConnection } from "./ProtocolDagInspector";

type UseProtocolDagConnectArgs = {
  resolveKind: (nodeId: string) => ProtocolNodeKind | null;
  onAddConnection?: (connection: ProtocolConnection) => void;
  readOnly: boolean;
  setPendingConnection: (value: PendingConnection | null) => void;
  clearSelection: () => void;
};

/**
 * Build connect handlers that persist edges or open a type picker.
 *
 * @param args - Kind resolver, mutation callback, and selection setters
 * @returns Connection commit and React Flow onConnect handlers
 */
export function useProtocolDagConnect({
  resolveKind,
  onAddConnection,
  readOnly,
  setPendingConnection,
  clearSelection,
}: UseProtocolDagConnectArgs) {
  const commitConnection = useCallback(
    (
      fromNodeId: string,
      fromNodeType: string,
      toNodeId: string,
      toNodeType: string,
      connectionType: ProtocolConnectionType,
    ) => {
      if (!onAddConnection || readOnly) return;
      onAddConnection({
        connection_id: `${connectionType}:${fromNodeId}->${toNodeId}`,
        from_node_id: fromNodeId,
        from_node_type: fromNodeType,
        to_node_id: toNodeId,
        to_node_type: toNodeType,
        connection_type: connectionType,
        revision: 0,
      });
      setPendingConnection(null);
    },
    [onAddConnection, readOnly, setPendingConnection],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (readOnly || !connection.source || !connection.target) return;
      const fromKind = resolveKind(connection.source);
      const toKind = resolveKind(connection.target);
      if (!fromKind || !toKind) return;
      const options = compatibleConnectionTypes(fromKind, toKind);
      if (options.length === 0) return;
      if (options.length === 1) {
        commitConnection(
          connection.source,
          fromKind,
          connection.target,
          toKind,
          options[0],
        );
        return;
      }
      setPendingConnection({
        fromNodeId: connection.source,
        fromNodeType: fromKind,
        toNodeId: connection.target,
        toNodeType: toKind,
        options,
      });
      clearSelection();
    },
    [
      readOnly,
      resolveKind,
      commitConnection,
      setPendingConnection,
      clearSelection,
    ],
  );

  return { commitConnection, onConnect };
}
